<template>
  <v-timeline class="timeline-container" side="end" align="start">
    <template v-for="(report, index) in reports" :key="report.id || index">
      <v-timeline-item
        v-if="index == 0 || other_date(index)"
        size="small"
        dot-color="transparent"
        class="timeline-item"
      >
        <template #icon>
          <v-avatar size="24" :color="date_color(report)">
            <v-icon color="white" size="16">mdi-calendar</v-icon>
          </v-avatar>
        </template>
        <v-chip :color="date_color(report)" text-color="white">
          <span v-if="report.sticky">STICKY: </span>{{ report.date_format }}
        </v-chip>
      </v-timeline-item>
      <ReportTimeLineItem
        :report="report"
        :on-report-updated="onReportUpdated"
      />
    </template>
  </v-timeline>
</template>

<script>
import { defineComponent } from "vue";
import { useReportCategoryIcons } from "@/composables/useReportCategoryIcons";
import ReportTimeLineItem from "./ReportTimeLineItem.vue";

export default defineComponent({
  name: "ReportTimeline",
  components: {
    ReportTimeLineItem,
  },
  props: {
    reports: {
      type: Array,
      required: true,
      default: () => [],
    },
    onReportUpdated: {
      type: Function,
      default: null,
    },
  },
  setup(props) {
    const { getCategoryIcon } = useReportCategoryIcons();

    const other_date = (index) => {
      if (index == 0) return false;
      var d1 = new Date(props.reports[index].date);
      var d2 = new Date(props.reports[index - 1].date);

      return (
        d1.getFullYear() !== d2.getFullYear() ||
        d1.getMonth() !== d2.getMonth() ||
        d1.getDate() !== d2.getDate()
      );
    };

    const date_color = (report) => {
      return report.sticky ? "green" : "red";
    };

    return {
      getCategoryIcon,
      other_date,
      date_color,
    };
  },
});
</script>

<style>
/* Minimal styles to ensure consistent width and proper appearance */
.timeline-container {
  width: 100%;
}

.timeline-item {
  width: 100%;
}

/* Make sure the timeline body takes full width */
.v-timeline-item__body {
  width: 100%;
  max-width: calc(100% - 48px);
  padding-right: 16px;
  padding-top: 0px;
}

/* Fix icon in timeline dot alignment */
.v-timeline-item__inner-dot {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px !important;
  height: 24px !important;
}

/* Ensure the v-timeline has the correct orientation */
.v-timeline {
  position: relative;
}

/* Ensure proper spacing for timeline items */
.v-timeline-item {
  min-height: 48px;
  padding-bottom: 16px;
}

/* Style the timeline dot */
.v-timeline-item__dot {
  width: 24px !important;
  height: 24px !important;
  margin: 0 !important;
}

/* Style the timeline line */
.v-timeline:before {
  width: 2px;
  background: rgba(0, 0, 0, 0.12);
}

/* 📱 MOBILE-ONLY OPTIMIZATIONS (xs breakpoint: 0-599px) */
@media (max-width: 599px) {
  /* Reduce timeline item spacing */
  .v-timeline-item {
    min-height: 28px !important;
    padding-bottom: 6px !important;
  }

  /* MAXIMUM SPACE UTILIZATION: Maximize timeline body width */
  .v-timeline-item__body {
    max-width: calc(
      100% - 20px
    ) !important; /* Account for dot and minimal spacing */
    width: calc(100% - 20px) !important; /* Force full width usage */
    padding-right: 0px !important; /* Remove right padding for max space */
    padding-left: 0px !important; /* Remove left padding for max space */
    margin-left: 20px !important; /* Position body just after the dot with minimal gap */
  }

  /* Ultra-compact timeline dots for mobile - positioned at far left */
  .v-timeline-item__dot {
    width: 16px !important; /* Reduced from 20px to 16px */
    height: 16px !important;
    left: 4px !important; /* Position dots at far left */
    margin-left: 0 !important;
  }

  .v-timeline-item__inner-dot {
    width: 16px !important;
    height: 16px !important;
  }

  /* SPACE OPTIMIZATION: Override Vuetify timeline default positioning */
  .v-timeline-item__opposite {
    display: none !important; /* Hide opposite content to save space */
  }

  /* Minimal timeline line positioned at far left */
  .v-timeline:before {
    width: 1px !important;
    left: 4px !important; /* Move timeline line closer to left edge */
  }

  /* SPACE OPTIMIZATION: Eliminate left margin/padding from timeline container */
  .v-timeline {
    margin-left: 0 !important;
    padding-left: 0 !important;
  }

  /* SPACE OPTIMIZATION: Position timeline items closer to left edge */
  .v-timeline-item {
    margin-left: 0 !important;
    padding-left: 0 !important;
  }

  /* Ultra-tight date chip spacing */
  .v-timeline-item .v-chip {
    font-size: 0.7rem !important; /* Reduced from 0.75rem */
    height: 22px !important; /* Reduced from 24px */
    margin-bottom: 2px !important; /* Reduced from 4px */
  }

  /* SPACE OPTIMIZATION: Tighter icon positioning */
  .v-timeline-item__icon {
    margin-right: 4px !important; /* Minimal gap between icon and content */
  }

  /* SPACE OPTIMIZATION: Date chip container */
  .v-timeline-item .v-chip {
    margin-left: 0 !important;
    margin-right: 2px !important;
  }

  /* AGGRESSIVE LEFT SPACE ELIMINATION: Override all Vuetify timeline defaults */
  .timeline-container {
    margin-left: 0 !important;
    padding-left: 0 !important;
  }

  .timeline-container .v-timeline {
    margin-left: 0 !important;
    padding-left: 0 !important;
  }

  /* Final override for any remaining left positioning */
  .v-timeline-item__divider {
    margin-left: 0 !important;
    padding-left: 0 !important;
  }
}
</style>
